import { useInfiniteQuery } from '@tanstack/react-query';

import VendorsService from '~/services/VendorsService';

export const propertyRelationsQueryKeys = {
  all: ['propertyRelations'] as const,
  search: (searchId: string, propertyId?: string) =>
    [...propertyRelationsQueryKeys.all, searchId, ...(propertyId ? [propertyId] : [])] as const,
};

interface PropertyRelation {
  property_id: string | null;
  vendor_id: string | null;
  property_name: string | null;
  room_type_id: string | null;
  room_type_code: string | null;
  room_type_name: string | null;
  room_rate_id: string | null;
  rate_plan_id: string | null;
  rate_plan_code: string | null;
  rate_plan_name: string | null;
  row_index: string;
}

interface PropertyRelationsApiResponse {
  result: Array<PropertyRelation>;
  count: number;
  total: number;
}

interface PropertyRelationsPage {
  rows: Array<PropertyRelation>;
  total: number;
}

/**
 * Incrementally fetches pages of property relations for infinite scroll.
 * Each scroll-to-bottom triggers fetchNextPage() until all rows are loaded.
 */
export function useGetPropertyRelations(searchId: string, pageSize: number, propertyId?: string) {
  return useInfiniteQuery({
    queryKey: propertyRelationsQueryKeys.search(searchId, propertyId),
    queryFn: async ({ pageParam }: { pageParam: number }): Promise<PropertyRelationsPage> => {
      const offset = pageParam * pageSize;
      const response: PropertyRelationsApiResponse = await VendorsService.getRelationsBySearchId(
        searchId,
        offset,
        pageSize,
        propertyId,
      );
      return {
        rows: response.result ?? [],
        total: response.total ?? 0,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, p) => sum + p.rows.length, 0);
      return loadedCount < lastPage.total ? allPages.length : undefined;
    },
    enabled: !!searchId,
  });
}

export type { PropertyRelation };
